import _ from 'lodash'
import {Dispatch, SetStateAction, useRef, useState, useEffect, useMemo} from 'react'
import {GarminGIcon, CloseIcon, GarminLIcon, ChevronUpIcon, ChevronDownIcon} from '../../asset/image'
import {createTaskColorTranslator, RIF} from '../../engine'
import {AdherenceViewTag, GarminDirect, getTaskContentName, HandleToggleTaskPayload} from '../../model'
import {ITask, TaskType} from '../../shared/db'
import {selectTheme, selectMethod} from '../../store'
import {Checkbox} from '../atoms'
import {AdherenceViewedByType} from '..'

interface ViewTagsWidthListItem {
  id?: string
  name: string
  width: number
}

export const TaskCompletionContentManageBar = (props: {
  viewTagList: AdherenceViewTag[]
  setViewTagList: Dispatch<SetStateAction<AdherenceViewTag[]>>
  adherenceViewedByType: AdherenceViewedByType
  setAdherenceViewedByType: Dispatch<SetStateAction<AdherenceViewedByType>>
  garminConnectEnabled?: boolean
  garminDirectEnabled?: boolean
  viewDatatype?: GarminDirect
}) => {
  const {color, fontSize, fontWeight} = selectTheme()
  const colorTranslator = createTaskColorTranslator()
  const {
    viewTagList,
    setViewTagList,
    adherenceViewedByType,
    setAdherenceViewedByType,
    garminConnectEnabled,
    garminDirectEnabled,
    viewDatatype,
  } = props
  const viewMenuRef = useRef<HTMLDivElement>(null)
  const method = selectMethod()
  const [displayViewMenu, setDisplayViewMenu] = useState<boolean>(false)
  const [viewTagsOverflow, setViewTagsOverflow] = useState<boolean>(false)
  const [viewTagsWidthList, setViewTagsWidthList] = useState<ViewTagsWidthListItem[]>([])
  const [expandingViewTagsList, setExpandingViewTagsList] = useState<boolean>(false)

  useEffect(() => {
    const totalViewTagsWidth = _.sumBy(viewTagsWidthList, (item) => {
      return item.width
    })
    setViewTagsOverflow(totalViewTagsWidth > window.innerWidth - 202.5)
  }, [viewTagsWidthList, window.innerWidth])

  const handleToggleViewMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.currentTarget.id === 'viewMenuToggle') setDisplayViewMenu(!displayViewMenu)
  }

  const ViewTags = (props: {task: AdherenceViewTag}) => {
    const {color, fontSize} = selectTheme()

    const tagRef = useRef<HTMLDivElement>(null)
    const {task} = props

    useEffect(() => {
      if (tagRef.current) {
        const width = tagRef.current.getBoundingClientRect().width + 5
        if (!_.find(viewTagsWidthList, ['id', task.id])) {
          setViewTagsWidthList((prev) => [...prev, {id: task.id, name: task.name, width}])
        }
      }
    }, [])

    const IconContent =
      task?.name === 'Garmin Connect App' ? (
        <img src={GarminGIcon} alt='Garmin Connect data' width='18' />
      ) : task?.name === 'Labfront Companion App' ? (
        'L'
      ) : task?.name === 'Dexcom App' ? (
        'D'
      ) : (
        (task.index ?? 0) + 1
      )

    const handleClickCancel = () => {
      if (viewTagList?.length === 1) return // not allow to cancel when last remains
      // cancel garmin by name, cause garmin name will not repeat
      if (task?.name === 'Garmin Connect App' || task?.name === 'Labfront Companion App') {
        return setViewTagList(viewTagList.filter((obj) => obj.name !== task.name))
      }
      setViewTagList(viewTagList.filter((obj) => obj.id !== task.id))
    }

    return (
      <div
        ref={tagRef}
        css={{
          height: '32px',
          border: `1px solid ${color.grey_160}`,
          borderRadius: '30px',
          padding: '0 8px',
          background: 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
        }}>
        <div
          aria-label={task?.alt}
          role='img'
          css={{
            background: task?.color,
            borderRadius: '50%',
            width: '18px',
            height: '18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: color.white,
            fontSize: '12px',
          }}>
          {IconContent}
        </div>
        <span
          css={{
            margin: '0 8px',
            fontSize: fontSize.h7,
          }}>
          {task?.name}
        </span>
        <button
          onClick={handleClickCancel}
          aria-label={'Remove this task/data type from adherence table'}
          css={{
            width: '24px',
            height: '24px',
            backgroundImage: `url(${CloseIcon})`,
            backgroundSize: '10px',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
          }}
        />
      </div>
    )
  }

  const ViewMenu = () => {
    const garminDeviceTask: ITask | undefined = _.find(method?.taskList, {type: TaskType.GarminDevice}) // TODO: check type
    const viewMenuTaskList = useMemo(() => {
      if (!method?.taskList) return []
      const result = method.taskList
        .filter((t) => t.type !== TaskType.GarminDevice && t.enabled)
        .map((t) => ({
          id: t.id,
          name: t[getTaskContentName(t.type)]?.name ?? '',
          color: t.color ? `#${t.color}` : colorTranslator(t.index ?? 0),
        }))
      if (method.dexcomIntegrationId) {
        result.splice(0, 0, {
          id: method.dexcomIntegrationId,
          name: 'Dexcom App',
          color: color.dexcomGreen,
        })
      }
      return result
      // return result.map((item, index) => ({...item, index}))
    }, [method])

    const handleChange = (checked: boolean, obj: HandleToggleTaskPayload) => {
      if (viewTagList.length === 1 && !checked) return // not allow to remove last tag
      if (obj.type === 'task' && !checked) {
        return setViewTagList(viewTagList.filter((item) => item.id !== obj.payload.id))
      }
      if (obj.type === 'garmin' && !checked) {
        return setViewTagList(viewTagList.filter((item) => item.name !== obj.payload.name))
      }
      return setViewTagList([...viewTagList, obj.payload])
    }

    return (
      <div
        id='viewMenu'
        css={{
          width: '315px',
          border: `1px solid ${color.border.disabled}`,
          boxShadow: '0px 4px 10px 1px rgba(104, 104, 104, 0.25)',
          borderRadius: '5px',
          background: color.white,
          cursor: 'auto',
          padding: '20px',
          paddingBottom: '5px',
        }}>
        {/* switch */}
        <div css={{borderBottom: `1px solid ${color.grey_100}`, display: 'flex', marginBottom: '20px'}}>
          <div
            onClick={() => setAdherenceViewedByType(AdherenceViewedByType.Tasks)}
            css={{
              color: adherenceViewedByType === AdherenceViewedByType.Tasks ? color.primary : color.black,
              borderBottom:
                adherenceViewedByType === AdherenceViewedByType.Tasks ? `2px solid ${color.primary}` : 'none',
              paddingBottom: '15px',
              fontWeight: fontWeight.bold,
              marginRight: '30px',
              cursor: 'pointer',
            }}>
            By Tasks
          </div>
        </div>
        {/* content of view menu */}
        <div>
          {RIF(
            garminConnectEnabled,
            <label
              css={{
                display: 'flex',
                alignItems: 'start',
                width: 'fit-content',
                marginBottom: '15px',
                cursor: 'pointer',
              }}>
              <Checkbox
                value={viewTagList?.some((obj) => obj.name === 'Garmin Connect App')}
                onChange={(checked: boolean) =>
                  handleChange(checked, {
                    type: 'garmin',
                    payload: {
                      name: 'Garmin Connect App',
                      color: color.black,
                    },
                  })
                }
                // disableCheck={handleDisableCheck({
                //   type: 'garmin',
                //   payload: {
                //     name: 'Garmin Connect App',
                //     color: color.black,
                //   },
                // })}
              />
              <div css={{margin: 'auto 6px', marginTop: '-2px'}}>Garmin Connect App</div>
              <div
                css={{
                  borderRadius: '50%',
                  width: '13px',
                  height: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <img src={GarminGIcon} width='13' />
              </div>
            </label>,
          )}
          {RIF(
            garminDirectEnabled,
            <label
              css={{
                display: 'flex',
                alignItems: 'start',
                width: 'fit-content',
                marginBottom: '15px',
                cursor: 'pointer',
              }}>
              <Checkbox
                value={viewTagList?.some((obj) => obj.name === 'Labfront Companion App')}
                onChange={(checked: boolean) =>
                  handleChange(checked, {
                    type: 'garmin',
                    payload: {
                      name: 'Labfront Companion App',
                      id: garminDeviceTask?.id,
                      color: color.primary,
                    },
                  })
                }
                // disableCheck={handleDisableCheck({
                //   type: 'garmin',
                //   payload: {
                //     name: 'Labfront Companion App',
                //     id: garminDeviceTask?.id,
                //     color: color.primary,
                //   },
                // })}
              />
              <div css={{margin: 'auto 6px', marginTop: '-2px'}}>Labfront Companion App</div>
              <div
                css={{
                  background: color.primary,
                  color: color.white,
                  borderRadius: '50%',
                  fontSize: '7px',
                  width: '13px',
                  height: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  paddingRight: '2px',
                  paddingBottom: '1px',
                  justifyContent: 'center',
                }}>
                <img src={GarminLIcon} width='13' />
              </div>
            </label>,
          )}
          {viewMenuTaskList.map((item) => (
            <label
              key={item.id}
              css={{
                display: 'flex',
                alignItems: 'start',
                width: 'fit-content',
                marginBottom: '15px',
                cursor: 'pointer',
              }}>
              <Checkbox
                value={viewTagList?.some((obj) => obj.id === item.id)}
                onChange={(checked: boolean) => handleChange(checked, {type: 'task', payload: item})}
                // disableCheck={handleDisableCheck({type: 'task', payload: item})}
              />
              <div css={{margin: 'auto 6px', maxWidth: '89%', marginTop: '-2px'}}>{item.name}</div>
            </label>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div
      css={{
        backgroundColor: color.surface.grey.dark,
        borderRadius: '5px',
        // overflow: 'hidden',
        minHeight: '50px',
        position: 'relative',
      }}>
      {RIF(
        displayViewMenu,
        <div
          css={{
            position: 'absolute',
            top: 40,
            left: 8,
            zIndex: 100,
            transition: 'all 0.2s ease-in-out',
          }}>
          <ViewMenu />
        </div>,
      )}
      <div
        css={{
          width: '100%',
          display: 'flex',
          gap: '16px',
          padding: '8px',
        }}>
        <div
          onClick={handleToggleViewMenu}
          ref={viewMenuRef}
          id='viewMenuToggle'
          css={{
            paddingRight: '16px',
            borderRight: `1px solid ${color.border._160}`,
            minWidth: '80px',
            height: '32px',
            border: `1px solid ${displayViewMenu ? color.primary : color.border._160}`,
            backgroundColor: 'white',
            borderRadius: '5px',
            padding: '0 12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'relative',
            cursor: 'pointer',
            ':hover': {
              border: `1px solid ${displayViewMenu ? color.primary : color.black}`,
            },
          }}>
          <span id='viewMenuBrick' css={{color: displayViewMenu ? color.primary : ''}}>
            View
          </span>
          <div
            id='viewMenuBrick'
            css={{
              width: '10px',
              height: '10px',
              borderTop: `2px solid`,
              borderRight: `2px solid`,
              marginTop: displayViewMenu ? '5px' : '-5px',
              borderColor: displayViewMenu ? color.border.blue : color.border.default,
              rotate: displayViewMenu ? '-45deg' : '135deg',
              transition: 'rotate .2s ease-in-out',
              ':hover': {
                borderColor: displayViewMenu ? color.border.blue : color.border.black,
              },
            }}
          />
        </div>
        {/* view tags container */}
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '5px',
            overflow: expandingViewTagsList ? 'auto' : 'hidden',
            maxHeight: expandingViewTagsList ? '130px' : '37px',
          }}>
          {RIF(
            adherenceViewedByType === AdherenceViewedByType.Tasks && viewTagList,
            viewTagList.map((obj) => <ViewTags key={obj.id ?? obj.name} task={obj} />),
          )}
          {/* {RIF(
            viewDatatype &&
            adherenceViewedByType === AdherenceViewedByType.DeviceDataType,
            <div css={{display: 'flex'}}>{viewDatatype}</div>,
          )} */}
        </div>
      </div>
      {/* show more task button */}
      {RIF(
        viewTagsOverflow,
        <button
          onClick={() => setExpandingViewTagsList((prev) => !prev)}
          css={{
            width: '100%',
            height: '20px',
            minHeight: '20px',
            backgroundColor: color.surface.hover.white,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            cursor: 'pointer',
          }}>
          <p
            css={{
              color: color.textIcon.light,
              fontSize: fontSize.h7,
              marginRight: '4px',
            }}>
            Show {expandingViewTagsList ? 'fewer' : 'more'} tasks
          </p>
          <img src={expandingViewTagsList ? ChevronUpIcon : ChevronDownIcon} width='20' />
        </button>,
      )}
    </div>
  )
}
